import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, Users, MapPin, Phone, Mail, CheckCircle, ArrowRight,
  ArrowUpRight, FileCheck, ClipboardList, HardHat, Factory, Wrench,
  Cog, Briefcase, Hammer, Flame, Building2, Globe2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

/**
 * /russia-recruitment-agency-india
 * ────────────────────────────────────────────────────────────────────────
 * Russia-corridor recruitment landing page targeting the primary keyword
 * "Russia Recruitment Agency in India for Skilled Manpower".
 * Designed to outrank competitors for: Russia Recruitment Agency in India,
 * Russia Manpower Recruitment Agency, Recruitment Agency for Russia,
 * Russia Jobs Recruitment Agency, Skilled Manpower for Russia.
 */

const PRIMARY_PHONE = '+91-99588-48998';
const PRIMARY_EMAIL = 'info@zafco.in';
const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const ADVANTAGES = [
  { label: 'Government Licensed', detail: 'UDYAM-certified recruitment agency with full regulatory compliance and MEA registration' },
  { label: 'Skilled Workforce Database', detail: '50,000+ pre-screened candidates across construction, manufacturing, oil & gas, and engineering' },
  { label: 'Trade Testing Facilities', detail: 'On-site technical competency verification for masons, carpenters, welders, operators, and engineers' },
  { label: 'Fast Mobilization', detail: '30–45 day mobilization timeline from candidate selection to deployment in Russia' },
  { label: 'Pan-India Network', detail: 'Recruitment centres in Delhi NCR, UP, Rajasthan, Punjab, Haryana, Bihar, Maharashtra and 5 more states' },
  { label: 'Dedicated Russia Team', detail: 'Specialised team fluent in Russian business protocols and 2026 work-visa requirements' },
  { label: 'Documentation Support', detail: 'Full assistance with work permits, employment contracts and background verification' },
  { label: 'Visa Processing', detail: 'Expert guidance on Russia work visa pathways for Indian workers in 2026' },
  { label: 'Quality Screening', detail: 'Multi-stage screening: technical tests, medical exams and client interviews' },
  { label: 'End-to-End Management', detail: 'Single-point responsibility from sourcing to deployment, mobilization and post-arrival support' },
];

const INDUSTRY_GROUPS = [
  {
    icon: HardHat,
    title: 'Construction Manpower for Russia',
    intro: "Russia&rsquo;s construction sector faces acute shortages for residential, commercial and infrastructure projects. We supply:",
    roles: [
      'Mason (Bricklayer, Block Mason) — residential & commercial',
      'Carpenter (Joiner, Formworker) — structural & finish carpentry',
      'Steel Fixer (Rebar Worker) — reinforcement layout & binding',
      'Shuttering Carpenter (Scaffold Carpenter) — concrete formwork',
      'Tile Mason (Flooring Technician) — ceramic, porcelain, marble',
      'Painter (Decorator) — interior/exterior, surface preparation',
      'Scaffolder — scaffolding assembly & safety protocols',
      'Electrician — building wiring & electrical systems',
      'Plumber (Pipefitter) — water supply, drainage, HVAC piping',
    ],
  },
  {
    icon: Factory,
    title: 'Manufacturing & Industrial Workforce',
    intro: "Russia&rsquo;s manufacturing sector requires skilled operators for automotive, heavy engineering and process industries:",
    roles: [
      'Machine Operator — CNC, lathe, universal machine expertise',
      'Production Worker — assembly line & mass production',
      'Assembly Technician — mechanical & electronic assembly',
      'CNC Operator — computer-numerical-control machining',
      'Maintenance Technician — equipment troubleshooting & preventive maintenance',
    ],
  },
  {
    icon: Flame,
    title: 'Oil & Gas Workforce',
    intro: 'Pipeline construction, rig operations and maintenance face critical shortages. We supply:',
    roles: [
      'Pipe Fitter — pipeline alignment & welding preparation',
      'Mechanical Technician — equipment maintenance & repair',
      'Rig Workers (Rigman, Derrickman) — offshore/onshore rig operations',
      'Instrument Technician — process instrumentation & control systems',
      'Fabricator (Welder-Fabricator) — metal fabrication & structural welding',
    ],
  },
  {
    icon: Briefcase,
    title: 'Engineering Professionals',
    intro: 'Russian engineering firms require qualified professionals for power plants, infrastructure and industrial projects:',
    roles: [
      'Civil Engineer — B.E./B.Tech, infrastructure project experience',
      'Mechanical Engineer — process equipment, HVAC, manufacturing systems',
      'Electrical Engineer — power distribution, wiring, control systems',
      'QA/QC Engineer — quality assurance & quality control protocols',
      'Project Engineer — PMP-certified, multi-site project management',
    ],
  },
  {
    icon: Hammer,
    title: 'Welding Professionals',
    intro: 'Welding shortages impact construction, manufacturing and oil & gas. We supply certified welders:',
    roles: [
      'MIG Welder — Metal Inert Gas welding for steel & aluminium',
      'TIG Welder — Tungsten Inert Gas welding for precision applications',
      'ARC Welder — electric arc welding for structural & pipeline work',
      '6G Welder — pipe welding at 6G position (industry standard)',
      'Structural Welder — building & infrastructure steel welding',
    ],
  },
];

const PROCESS_STAGES = [
  { stage: '01', name: 'Requirement Analysis', timeline: '2–3 days' },
  { stage: '02', name: 'Candidate Sourcing', timeline: '5–7 days' },
  { stage: '03', name: 'CV Screening', timeline: '3–4 days' },
  { stage: '04', name: 'Trade Testing', timeline: '4–5 days' },
  { stage: '05', name: 'Client Interviews', timeline: '3–5 days' },
  { stage: '06', name: 'Medical Examination', timeline: '2–3 days' },
  { stage: '07', name: 'Documentation', timeline: '7–10 days' },
  { stage: '08', name: 'Visa Processing', timeline: '10–14 days' },
  { stage: '09', name: 'Mobilization', timeline: '3–5 days' },
  { stage: '10', name: 'Deployment & Support', timeline: 'Ongoing' },
];

const INDIA_ADVANTAGES = [
  { factor: 'Technical Competency', impact: "India&rsquo;s robust engineering education system produces highly trained professionals familiar with international safety standards." },
  { factor: 'Cost Efficiency', impact: '30–40% lower recruitment costs compared to Western/European markets.' },
  { factor: 'Workforce Availability', impact: '50,000+ skilled workers available across construction, manufacturing, and oil & gas.' },
  { factor: 'Communication Skills', impact: 'English-speaking workforce with basic Russian-language training options.' },
  { factor: 'Adaptability', impact: 'Proven track record of integrating into international work environments.' },
  { factor: 'International Experience', impact: 'Workers with experience in Middle East, Europe, and Africa projects.' },
  { factor: 'Strong Work Ethic', impact: 'Known for dedication, professionalism and quality commitment.' },
];

const SERVICES = [
  { title: 'Permanent Recruitment', body: 'Full-time placement with long-term retention focus. Ideal for core business positions requiring stability and continuity.' },
  { title: 'Bulk Hiring Solutions', body: 'Large-scale recruitment for 100–10,000 workers across multiple roles. Fast mobilization for construction, manufacturing and infrastructure.' },
  { title: 'Project-Based Recruitment', body: 'Temporary workforce for specific project durations (6 months to 3 years). Perfect for construction projects and plant commissioning.' },
  { title: 'Skilled Trades Recruitment', body: 'Specialised placement for certified masons, carpenters, welders, electricians, plumbers and operators with full trade-testing verification.' },
  { title: 'Executive Search', body: 'Headhunting for senior professionals: project managers, engineering directors and technical experts with 10+ years experience.' },
  { title: 'Turnkey Recruitment Solutions', body: 'End-to-end responsibility from sourcing to deployment — visa processing, documentation, travel and initial accommodation.' },
];

const REGIONS = [
  { region: 'Delhi NCR', strengths: 'Manufacturing workers, electricians, machine operators, engineering professionals' },
  { region: 'Uttar Pradesh', strengths: 'Construction workers (masons, carpenters), steel fixers, painters, plumbers' },
  { region: 'Rajasthan', strengths: 'Welders, fabricators, maintenance technicians, rig workers' },
  { region: 'Punjab', strengths: 'CNC operators, assembly technicians, mechanical technicians, engineers' },
  { region: 'Haryana', strengths: 'Industrial workers, production staff, QA/QC engineers, project engineers' },
  { region: 'Bihar', strengths: 'Bulk construction workforce, masons, tile masons, scaffolders' },
  { region: 'Jharkhand', strengths: 'Mining workers, pipe fitters, mechanical technicians, fabricators' },
  { region: 'Maharashtra', strengths: 'Engineering professionals, CNC operators, instrument technicians, engineers' },
  { region: 'Karnataka', strengths: 'Tech-savvy workers, electronic assembly, electrical engineers, QA engineers' },
  { region: 'Tamil Nadu', strengths: 'Manufacturing workers, assembly technicians, welders, machine operators' },
];

const FAQS = [
  { q: 'Do you recruit manpower for Russia from India?',
    a: "Yes, ZAFCO is a licensed Russia Recruitment Agency in India specialising in skilled manpower for construction, manufacturing, oil & gas, and engineering sectors. Our Russia desk currently mobilises hundreds of skilled candidates each year across project-bound and core-permanent mandates." },
  { q: 'What industries do you recruit for Russia?',
    a: 'We recruit for construction (masons, carpenters, steel fixers), manufacturing (machine operators, CNC operators), oil & gas (pipe fitters, rig workers), engineering (civil, mechanical, electrical engineers), and welding (MIG, TIG, ARC welders).' },
  { q: 'Can you provide bulk manpower for Russia?',
    a: "Yes, we offer bulk hiring solutions for 100–10,000 workers with 30–45 day mobilization. Russia&rsquo;s 2025 quota for Indian workers is 71,817, focusing on construction and manufacturing." },
  { q: 'How long does recruitment take?',
    a: 'Total timeline is 30–45 days from requirement analysis to deployment, including sourcing, trade testing, medical exams, visa processing, and mobilization.' },
  { q: 'What is the Russia work visa process for Indian workers in 2026?',
    a: 'Indian workers need a valid job offer from a Russian employer. The 2026 system offers structured pathways for skilled/technical specialists with work permits, employment contracts, and visa approval.' },
  { q: 'Do Indian workers need language skills for Russia?',
    a: 'Russian employers prefer English-speaking workers. Basic Russian-language training is available. A new "Skilled Visa" policy starting April 2026 requires NO language test.' },
  { q: 'What sectors face the biggest labor shortages in Russia?',
    a: 'Construction, manufacturing, metallurgy, logistics and IT sectors face critical shortages due to a shrinking population and outflow of residents.' },
  { q: 'Are your workers certified for international projects?',
    a: 'Yes, all skilled workers undergo trade testing for technical competency. Engineers hold B.E./B.Tech degrees with international safety standard knowledge.' },
  { q: 'Can you handle visa processing and documentation?',
    a: 'Yes, we provide full documentation support including work-permit application, employment contracts, background verification, and visa processing guidance.' },
  { q: 'What is the cost advantage of Indian manpower for Russia?',
    a: 'Indian workers offer 30–40% lower recruitment and employment costs compared to Western/European markets, with higher productivity and strong work ethic.' },
  { q: 'Do you provide medical examination for candidates?',
    a: 'Yes, all candidates undergo full medical check-ups at certified facilities before visa processing and deployment.' },
  { q: 'How does ZAFCO ensure candidate quality?',
    a: 'Multi-stage screening includes CV evaluation, trade testing, client interviews, medical exams, and 30-day performance monitoring after deployment.' },
  { q: 'How much does manpower recruitment for Russia cost?',
    a: "Recruitment cost depends on role mix, headcount, mobilization window and visa category. For most blue-collar and skilled-trade hires the engagement is on an employer-paid recruitment-fee model with zero candidate fees, fully MEA-compliant. Share your requirement and our Russia desk will revert with a transparent commercial proposal within one working day." },
  { q: 'Can Indian workers legally work in Russia?',
    a: 'Yes. Indian workers legally enter the Russian labour market under a valid work permit and work visa issued against a confirmed Russian-employer job offer. The 2026 "Skilled Visa" pathway provides a streamlined route for technical specialists and removes the Russian-language test for qualifying categories. ZAFCO handles all documentation, work-permit application, employment-contract notarisation and visa stamping.' },
  { q: 'What documents are required for Russia employment?',
    a: 'Standard documentation includes a valid Indian passport (minimum 18 months validity), the signed employment contract from the Russian employer, work-permit application package, police clearance certificate, medical fitness certificate from a designated centre, trade-test certification (for skilled trades), educational/qualification attestations and apostille where required, and the Russia work-visa application. ZAFCO assembles and submits the full dossier on the candidate&rsquo;s behalf.' },
  { q: 'How quickly can manpower be mobilized to Russia?',
    a: 'ZAFCO operates on a 30–45 day end-to-end timeline from signed requirement to candidates landing in Russia. For pre-screened standby talent pools (common in welding, civil and oil & gas trades) mobilization can compress to 20–25 days. Bulk projects of 100+ workers typically run on a rolling weekly mobilization plan agreed with the client.' },
  { q: 'Do you recruit welders for Russia?',
    a: 'Yes. Russia welder recruitment is one of our highest-volume verticals. We supply MIG, TIG, ARC, 6G pipeline and structural welders — all trade-tested and qualified to international procedure standards used by Russian EPC contractors, oil & gas operators and manufacturing OEMs.' },
  { q: 'Do you recruit construction workers for Russia?',
    a: "Yes. Russia construction-worker recruitment is ZAFCO's largest single category, covering masons, carpenters, steel fixers, shuttering carpenters, tile masons, painters, scaffolders, plumbers, electricians and general civil labour. We source through our pan-India network and trade-test every candidate at our regional centres before mobilization." },
];

const RussiaRecruitmentAgencyIndiaPage = () => {
  usePageMeta(
    'Russia Recruitment Agency in India for Skilled Manpower | ZAFCO',
    'Government-licensed Russia Recruitment Agency in India. Skilled Indian manpower for construction, manufacturing, oil & gas, engineering. 35+ years experience.',
    {
      canonical: '/russia-recruitment-agency-india',
      image: '/social-media/og-russia-recruitment-agency-india.jpg',
      imageAlt: 'Russia Recruitment Agency in India — ZAFCO supplies skilled Indian manpower for construction, manufacturing, oil & gas projects across Russia.',
    },
  );

  // BreadcrumbList + FAQPage + Service JSON-LD
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'International Recruitment',
          item: 'https://zafco.in/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
        { '@type': 'ListItem', position: 3, name: 'Russia Recruitment Agency in India',
          item: 'https://zafco.in/russia-recruitment-agency-india' },
      ],
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Russia Recruitment Agency in India for Skilled Manpower',
      description: 'Government-licensed overseas recruitment service supplying skilled Indian manpower (construction, manufacturing, oil & gas, engineering, welding) to Russian employers.',
      serviceType: 'Overseas Manpower Recruitment',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'Russia' },
        { '@type': 'Country', name: 'India' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Russian employers seeking skilled Indian manpower' },
      url: 'https://zafco.in/russia-recruitment-agency-india',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-russia-page', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [
      inject(breadcrumb, 'breadcrumb'),
      inject(faq, 'faq'),
      inject(service, 'service'),
    ];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const contactQuery = '?source=russia-recruitment-agency-india';

  return (
    <div data-testid="russia-recruitment-agency-india-page">
      <PageHeader
        title="Russia Recruitment Agency in India for Skilled Manpower"
        subtitle="Government-licensed overseas recruitment partner — supplying skilled Indian workforce to Russian construction, manufacturing, oil & gas and engineering employers."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'International Recruitment', path: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
          { label: 'Russia Recruitment Agency in India', path: '/russia-recruitment-agency-india' },
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved Recruitment Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* SECTION 1 — Intro */}
      <section className="py-16 bg-white" aria-labelledby="russia-intro">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Russia Corridor · 2026</span>
          <h2 id="russia-intro" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            ZAFCO — Russia Recruitment Agency in India for skilled manpower
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Russia faces a critical labour shortage with over <strong>1 million Indian workers</strong> needed by 2025–2026 to address workforce gaps in construction, manufacturing, metallurgy and logistics sectors. <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link> is your government-licensed <strong>Russia Recruitment Agency in India</strong>, connecting Russian employers with India&rsquo;s vast pool of skilled, disciplined and technically competent <strong>manpower</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Russian employers choose Indian workers for their exceptional technical expertise, strong work ethic, cost efficiency and adaptability to international projects. With <strong>35+ years</strong> of international recruitment experience, ZAFCO delivers end-to-end recruitment support — from requirement analysis and candidate screening to visa processing, documentation and mobilization. This corridor is delivered as part of our broader{' '}
            <Link to="/international-manpower-recruitment-europe-russia-israel-saudi-arabia" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">
              International Manpower Recruitment programme
            </Link>{' '}covering Saudi Arabia, Europe, Russia and Israel.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 mt-8 text-gray-700">
            {[
              'Government-licensed recruitment agency with ISO certification',
              'Extensive database of 50,000+ skilled workers across India',
              'Trade-testing facilities for technical competency verification',
              'Pan-India recruitment network covering 12+ states',
              'Dedicated Russia recruitment team for direct, single-point communication',
              'Full documentation and visa processing assistance',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-3 mt-10">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="russia-hero-cta-request-manpower">
              <Link to={`/contact${contactQuery}-request-manpower`}>
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Request Manpower
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="russia-hero-cta-submit-requirement">
              <Link to={`/contact${contactQuery}-submit-requirement`}>
                Submit Job Requirement
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10" data-testid="russia-hero-cta-contact">
              <a href={`mailto:${PRIMARY_EMAIL}?subject=Russia%20recruitment%20enquiry`}>
                <Mail className="w-4 h-4 mr-2" aria-hidden="true" /> Contact Recruitment Team
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 1b — Understanding Russia's Workforce Requirements (E-E-A-T authority) */}
      <section className="py-16 bg-slate-50 border-t border-slate-200" aria-labelledby="russia-workforce-requirements">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Sector Outlook</span>
          <h2 id="russia-workforce-requirements" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Understanding Russia&rsquo;s workforce requirements
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Russia&rsquo;s skilled-labour gap is structural, not cyclical. Six sectors are driving sustained demand for Indian manpower in 2026. As an established <strong>Russia Recruitment Agency in India</strong>, ZAFCO aligns its sourcing pipelines, trade-testing protocols and mobilization plans to the specific role profiles each of these sectors requires.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: Building2,
                title: 'Infrastructure projects',
                body: 'Road, rail, bridge, metro and utilities programmes across Moscow, St. Petersburg, Kazan and the Far East require sustained civil-engineering workforces. Indian project engineers, surveyors, formwork crews and steel fixers are deployed on multi-year EPC contracts.',
              },
              {
                icon: HardHat,
                title: 'Construction workforce demand',
                body: 'Residential, commercial and industrial construction programmes face a persistent shortage of masons, carpenters, scaffolders and finishing tradesmen. Russian developers source Indian construction crews for both metropolitan high-rise projects and regional infrastructure builds.',
              },
              {
                icon: Factory,
                title: 'Industrial manufacturing requirements',
                body: 'Automotive, heavy engineering, metallurgy and process-industry plants demand CNC operators, assembly technicians, machine operators and production supervisors. India&rsquo;s manufacturing-trained workforce integrates cleanly into Russian production cultures.',
              },
              {
                icon: Flame,
                title: 'Oil & Gas workforce needs',
                body: 'Pipeline construction, refinery turnarounds, offshore platforms and gas-processing modules require pipe fitters, instrument technicians, fabricators, rig workers and HSE professionals. Indian oil-and-gas crews bring international project experience from the Middle East and West Africa.',
              },
              {
                icon: Briefcase,
                title: 'Engineering & technical manpower',
                body: 'Russian EPC contractors and OEMs hire Indian civil, mechanical, electrical, instrumentation and QA/QC engineers (B.E./B.Tech qualified) for project management, design coordination and commissioning roles on plant erection and infrastructure programmes.',
              },
              {
                icon: Hammer,
                title: 'Skilled trade shortages',
                body: "Welding (MIG / TIG / ARC / 6G), electrical, plumbing, HVAC and finishing trades face the deepest shortages across Russia&rsquo;s industrial heartlands. ZAFCO&rsquo;s trade-tested skilled-trade pipeline is the largest single category of our Russia recruitment volume.",
              },
            ].map((sector) => {
              const Icon = sector.icon;
              return (
                <div key={sector.title} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-amber-400 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl flex-shrink-0">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zafco-blue mb-1.5">{sector.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{sector.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-gray-700 leading-relaxed mt-8 text-sm">
            Whether your project is single-trade, multi-discipline or a complete turnkey workforce mandate, ZAFCO&rsquo;s Russia desk matches your sector profile to a vetted, trade-tested candidate pipeline calibrated to Russian site protocols and 2026 visa pathways.
          </p>
        </div>
      </section>

      {/* SECTION 2 — Why Choose ZAFCO (advantage matrix) */}
      <section className="py-16 bg-slate-50" aria-labelledby="why-zafco">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-zafco" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Why choose ZAFCO for Russia recruitment?
          </h2>
          <p className="text-gray-700 mb-10 max-w-3xl">
            ZAFCO stands apart as the premier <strong>Russia Recruitment Agency in India</strong> through our proven expertise, full-cycle services and commitment to quality. Russian employers consistently choose us for the ten reasons below.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ADVANTAGES.map((a, idx) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.35, delay: idx * 0.04 }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-amber-300 transition-colors"
              >
                <h3 className="text-base font-bold text-zafco-blue mb-2">{a.label}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{a.detail}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 p-6 bg-white rounded-2xl border-l-4 border-amber-500">
            <p className="text-gray-700 leading-relaxed">
              ZAFCO is an ISO-certified, Government-of-India approved recruitment agency with <strong>50+ years of combined expertise</strong> in international manpower supply. Our rigorous screening process ensures every candidate meets Russian employer standards for technical competency, work ethic and reliability — we have mobilised crews across construction sites, manufacturing plants and oil &amp; gas projects in multiple Russian regions.
            </p>
            <div className="mt-5">
              <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="russia-section2-cta">
                <Link to={`/contact${contactQuery}-trust`}>Contact Recruitment Team <ArrowUpRight className="w-4 h-4 ml-1" aria-hidden="true" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Industries */}
      <section className="py-16 bg-white" aria-labelledby="industries-russia">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="industries-russia" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Industries we recruit for Russia
          </h2>
          <p className="text-gray-700 mb-10 max-w-3xl">
            ZAFCO provides specialised manpower for all major industries facing labour shortages in Russia. Our candidates bring technical expertise, international safety-standard knowledge and proven project experience.
          </p>

          {/* Russia-specific imagery with WebP-first delivery for Core Web Vitals */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {[
              { base: 'russia-recruitment-agency-india', alt: 'Russia Recruitment Agency in India — skilled Indian construction workers deployed to Russian infrastructure projects by ZAFCO' },
              { base: 'russia-manpower-recruitment', alt: 'Russia manpower recruitment — Indian welders, pipe fitters and CNC operators for Russian manufacturing and oil & gas plants' },
              { base: 'skilled-manpower-russia', alt: 'Skilled manpower for Russia — ZAFCO trade-tested Indian workforce mobilized for Russian oil & gas, construction and engineering projects' },
            ].map((img) => (
              <figure key={img.base} className="bg-slate-100 rounded-2xl overflow-hidden border border-slate-200">
                <picture>
                  <source srcSet={`/social-media/${img.base}.webp`} type="image/webp" />
                  <img
                    src={`/social-media/${img.base}.jpg`}
                    alt={img.alt}
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="338"
                    className="w-full h-44 object-cover"
                    data-filename={`${img.base}.webp`}
                  />
                </picture>
              </figure>
            ))}
          </div>

          <div className="space-y-8">
            {INDUSTRY_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.title} className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="bg-zafco-blue text-white p-3 rounded-xl">
                      <Icon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-zafco-blue">{group.title}</h3>
                      <p className="text-gray-700 mt-1">{group.intro}</p>
                    </div>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2 mt-4 text-gray-700">
                    {group.roles.map((role) => (
                      <li key={role} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="russia-industries-cta-submit">
              <Link to={`/contact${contactQuery}-submit-after-industries`}>
                <ClipboardList className="w-4 h-4 mr-2" aria-hidden="true" /> Submit Job Requirement
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Recruitment Process */}
      <section className="py-16 bg-slate-900 text-white" aria-labelledby="process-russia">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="process-russia" className="text-3xl md:text-4xl font-bold font-syne mb-3">
            Russia manpower recruitment process
          </h2>
          <p className="text-slate-300 mb-10 max-w-3xl">
            ZAFCO follows a professionally structured 10-stage recruitment timeline ensuring quality, compliance and fast mobilization. <strong className="text-amber-400">Total timeline: 30–45 days</strong> from requirement to deployment.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {PROCESS_STAGES.map((s) => (
              <div key={s.stage} className="bg-slate-800/70 border border-slate-700 rounded-xl p-4">
                <div className="text-amber-400 text-sm font-bold tracking-wider">{s.stage}</div>
                <div className="font-semibold text-white mt-1">{s.name}</div>
                <div className="text-slate-400 text-xs mt-1">{s.timeline}</div>
              </div>
            ))}
          </div>
          <p className="text-slate-400 text-sm mt-8 max-w-3xl">
            Each stage includes quality checkpoints and client communication to ensure transparency and satisfaction.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid="russia-process-cta-quote">
              <Link to={`/contact${contactQuery}-quote`}>
                <FileCheck className="w-4 h-4 mr-2" aria-hidden="true" /> Get Recruitment Quote
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Why Indian Workers (India advantages) */}
      <section className="py-16 bg-white" aria-labelledby="india-advantages">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="india-advantages" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Skilled manpower supply for Russia — India&rsquo;s advantages
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            India offers Russia a unique combination of workforce advantages that directly address the country&rsquo;s labour crisis. Russia&rsquo;s official 2025 quota for Indian workers is <strong>71,817</strong>, focusing on metallurgy, construction, logistics and retail — reflecting growing diplomatic and economic ties.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-base">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Advantage</th>
                  <th className="text-left px-5 py-3 font-semibold">Impact for Russian employers</th>
                </tr>
              </thead>
              <tbody>
                {INDIA_ADVANTAGES.map((row, idx) => (
                  <tr key={row.factor} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-5 py-3 font-medium text-zafco-blue">{row.factor}</td>
                    <td className="px-5 py-3 text-gray-700">{row.impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTION 5b — Russia Labour Market Data 2025-26 (E-E-A-T, data-backed) */}
      <section className="py-16 bg-white border-t border-slate-200" aria-labelledby="russia-labour-data">
        <div className="container mx-auto px-6 max-w-6xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Market context · Russia 2025–26</span>
          <h2 id="russia-labour-data" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Russia&rsquo;s labour shortage — the numbers driving Indian recruitment demand
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            Russia faces an unprecedented structural workforce gap. A combination of demographic decline, accelerated infrastructure investment and large-scale industrial commissioning has created sustained demand for skilled Indian manpower across construction, metallurgy, manufacturing, oil &amp; gas and logistics. The data below summarises the official quotas and projected requirements that frame our Russia recruitment service.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { stat: '1M+', label: 'Indian workers needed by 2025–26', detail: 'Across construction, manufacturing, metallurgy and logistics' },
              { stat: '71,817', label: 'Official 2025 quota for Indian workers', detail: 'Russian Government allocation — metallurgy, construction, logistics, retail' },
              { stat: '30–45', label: 'Day mobilization timeline (ZAFCO)', detail: 'Requirement analysis → trade test → visa → deployment' },
              { stat: '30–40%', label: 'Cost advantage of Indian manpower', detail: 'Versus Western/European recruitment markets' },
            ].map((d) => (
              <div key={d.label} className="bg-slate-50 rounded-2xl p-5 border-l-4 border-amber-500">
                <div className="text-3xl font-bold text-zafco-blue">{d.stat}</div>
                <div className="text-sm font-semibold text-gray-800 mt-1">{d.label}</div>
                <div className="text-xs text-gray-600 mt-1 leading-relaxed">{d.detail}</div>
              </div>
            ))}
          </div>
          <div className="bg-zafco-blue/5 rounded-2xl p-6 border border-zafco-blue/20">
            <h3 className="font-bold text-zafco-blue mb-3 text-lg">Why this matters for Russian employers</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Russia&rsquo;s shrinking working-age population, combined with the outflow of residents and an aging workforce, has produced a sustained, multi-year requirement for foreign skilled labour. The 2026 introduction of a streamlined &quot;Skilled Visa&quot; pathway — which removes the Russian-language test requirement for technical specialists — has materially shortened the time-to-deploy for Indian workers compared to the legacy work-permit route.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              For Russian employers, partnering with an established <strong>Russia Manpower Recruitment Agency</strong> in India delivers three structural advantages: (1) access to a 50,000+ pre-screened candidate database, (2) consolidated trade-testing, medical and documentation infrastructure pre-mapped to Russian employer benchmarks, and (3) single-point accountability across the recruitment, visa and mobilization lifecycle. This is the operational model ZAFCO has refined across 35+ years of overseas recruitment.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Indian workers consistently demonstrate adaptability to Russian climate conditions, international safety standards (OSHA, EN, GOST equivalents) and disciplined site-management cultures — making them the preferred international workforce for Russian construction giants, manufacturing OEMs and EPC contractors.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Services */}
      <section className="py-16 bg-slate-50" aria-labelledby="services-russia">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="services-russia" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Our recruitment services for Russia
          </h2>
          <p className="text-gray-700 mb-10 max-w-3xl">
            ZAFCO offers full-spectrum overseas recruitment solutions matched to every Russian employer need — from permanent core hires to large-scale project-based mobilization.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <div key={s.title} className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="bg-amber-100 text-amber-700 inline-flex p-2 rounded-lg mb-3">
                  <Wrench className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-zafco-blue mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — Recruitment Locations Across India */}
      <section className="py-16 bg-white" aria-labelledby="india-regions">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="india-regions" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Recruitment locations across India
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            ZAFCO&rsquo;s pan-India network ensures access to India&rsquo;s widest skilled workforce. Each region has dedicated recruitment centres with local trade-testing facilities.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-base">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Region</th>
                  <th className="text-left px-5 py-3 font-semibold">Sourcing strengths</th>
                </tr>
              </thead>
              <tbody>
                {REGIONS.map((row, idx) => (
                  <tr key={row.region} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-5 py-3 font-medium text-zafco-blue flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-600" aria-hidden="true" /> {row.region}
                    </td>
                    <td className="px-5 py-3 text-gray-700">{row.strengths}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Why Partner with ZAFCO + Final CTA */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-slate-900 text-white" aria-labelledby="why-partner">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-partner" className="text-3xl md:text-4xl font-bold font-syne mb-3">
            Why partner with ZAFCO — your Russia Recruitment Agency in India
          </h2>
          <p className="text-slate-300 mb-8 max-w-3xl">
            Russia&rsquo;s plan to recruit <strong className="text-amber-400">1 million Indian workers by 2025</strong> reflects the urgent need for reliable recruitment agencies like ZAFCO. We&rsquo;re positioned to meet this demand with quality, speed and compliance.
          </p>
          <ul className="grid md:grid-cols-2 gap-3 mb-10">
            {[
              ['Recruitment Expertise', '35+ years of international manpower supply with proven Russia success'],
              ['Candidate Quality', 'Multi-stage screening with trade testing, medical exams and client interviews'],
              ['Speed', '30–45 day mobilization timeline from requirement to deployment'],
              ['Compliance', 'Government-licensed, ISO-certified with full regulatory adherence'],
              ['Client Support', 'Dedicated Russia team with 24/7 communication and single-point responsibility'],
              ['International Experience', 'Successful mobilization to Russia, Saudi Arabia, Europe and Israel'],
            ].map(([title, body]) => (
              <li key={title} className="flex items-start gap-3 bg-slate-800/40 p-4 rounded-xl border border-slate-700/60">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-semibold text-white">{title}</div>
                  <div className="text-slate-300 text-sm mt-0.5">{body}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to={`/contact${contactQuery}-final-employer`}
              className="bg-amber-500 text-slate-900 rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-amber-400 transition-colors"
              data-testid="russia-final-cta-employer"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-700">For Russian employers</span>
                <span className="text-lg">Start recruitment now</span>
              </span>
              <ArrowUpRight className="w-6 h-6" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${PRIMARY_PHONE.replace(/[^+\d]/g, '')}`}
              className="bg-slate-800/60 border border-slate-700 text-white rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-slate-800 transition-colors"
              data-testid="russia-final-cta-phone"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-400">Speak to our Russia desk</span>
                <span className="text-lg">{PRIMARY_PHONE}</span>
              </span>
              <Phone className="w-6 h-6 text-amber-400" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 9 — Contact Form */}
      <section className="py-16 bg-white" aria-labelledby="russia-contact-form">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Talk to our Russia desk</span>
            <h2 id="russia-contact-form" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
              Request manpower indent or sourcing brief
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Share your manpower requirement — role mix, headcount, deployment window, project location.
              Our dedicated Russia team responds within one working day with a sourcing & mobilization plan.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 md:p-8 max-w-3xl mx-auto" data-theme="light">
            <ConsultationForm sourceTag="russia-recruitment-agency-india" />
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQs */}
      <section className="py-16 bg-slate-50" aria-labelledby="faqs-russia">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="faqs-russia" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Frequently asked questions
          </h2>
          <p className="text-gray-700 mb-8">
            Answers to the most common questions Russian employers and Indian candidates ask about our Russia recruitment service.
          </p>
          <div className="space-y-4">
            {FAQS.map((f, idx) => (
              <details key={f.q} className="bg-white rounded-xl border border-slate-200 p-5 group" data-testid={`russia-faq-${idx}`}>
                <summary className="cursor-pointer font-semibold text-zafco-blue list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-amber-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-gray-700 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 — Related Resources (internal linking) */}
      <section className="py-12 bg-white border-t border-slate-200" aria-labelledby="related-resources">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="related-resources" className="text-xl font-bold text-zafco-blue mb-5">
            Related recruitment resources
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/international-manpower-recruitment-europe-russia-israel-saudi-arabia" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-intl-pillar">
              <Globe2 className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">International Manpower Recruitment</div>
                <div className="text-xs text-gray-600 mt-0.5">Saudi · Europe · Russia · Israel</div>
              </div>
            </Link>
            <Link to="/eastern-europe-recruitment-india" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-ee-hub">
              <Globe2 className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Eastern Europe Recruitment Hub</div>
                <div className="text-xs text-gray-600 mt-0.5">Russia · Poland · Czechia · Slovakia · Romania</div>
              </div>
            </Link>
            <Link to="/saudi-arabia" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-saudi">
              <Building2 className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Saudi Arabia Recruitment</div>
                <div className="text-xs text-gray-600 mt-0.5">Detailed Saudi corridor services</div>
              </div>
            </Link>
            <Link to="/germany-opportunity-card" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-germany">
              <Users className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Germany Opportunity Card</div>
                <div className="text-xs text-gray-600 mt-0.5">European corridor (visa pathway)</div>
              </div>
            </Link>
            <Link to="/careers" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-careers">
              <Briefcase className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Active overseas openings</div>
                <div className="text-xs text-gray-600 mt-0.5">Submit your profile</div>
              </div>
            </Link>
            <Link to="/about" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-about">
              <Cog className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">About ZAFCO HRM</div>
                <div className="text-xs text-gray-600 mt-0.5">35+ years overseas recruitment</div>
              </div>
            </Link>
            <Link to="/contact" className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-amber-400 transition-colors" data-testid="russia-related-contact">
              <Mail className="w-5 h-5 text-zafco-blue flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <div className="font-semibold text-zafco-blue text-sm">Contact recruitment team</div>
                <div className="text-xs text-gray-600 mt-0.5">{PRIMARY_EMAIL}</div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RussiaRecruitmentAgencyIndiaPage;
